// import React, { useState, useEffect } from 'react'

import React, { useState, useEffect } from 'react'
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Dropdown,
  InputGroup,
  Nav,
  Badge,
  Pagination,
  Card
} from 'react-bootstrap'
import { Search, Grid, List, X } from 'react-bootstrap-icons'
import { MemberCard } from '../Components/MemberPage/Member-card.jsx'
import Header from '../Components/Header.jsx'
import StudentSdebar from './StudentSdebar.jsx'
import axios from 'axios'
import { debounce } from 'lodash'
import { motion } from 'framer-motion'

export default function MembersPage() {
  const [members, setMembers] = useState([])
  const [filteredMembers, setFilteredMembers] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [view, setView] = useState('grid')
  const [currentPage, setCurrentPage] = useState(1)
  const [sortOption, setSortOption] = useState('Recently Active') // Default sort option
  const itemsPerPage = 8
  const [sidebarExpanded, setSidebarExpanded] = useState(false);

  // Sorting Options (Dynamic)
  const sortingOptions = ['Recently Active', 'Newest Members', 'Alphabetical']

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true)
      const token = localStorage.getItem('jwttoken')
      if (!token) return

      try {
        const response = await axios.get(
          'https://kaasbackend.vetrinahealthcare.com/socialapp/users/',
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        )

        if (response.status === 200) {
          // Make sure the API response includes the 'is_connected' status
          const membersWithStatus = response.data.map((member) => ({
            ...member,
            is_friends: member.is_friends ?? false, // Default to false if not provided
          }))
          setMembers(membersWithStatus)
          setFilteredMembers(membersWithStatus)
        }
      } catch (error) {
        console.error('Error fetching user data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchUserData()
  }, [])

  // Search Function (Optimized with Debounce)
  const handleSearch = debounce((query) => {
    setSearchTerm(query)
    if (!query) {
      setFilteredMembers(members)
      return
    }
    const lowerCaseQuery = query.toLowerCase()
    const filtered = members.filter((member) =>
      `${member.first_name} ${member.last_name}`
        .toLowerCase()
        .includes(lowerCaseQuery)
    )
    setFilteredMembers(filtered)
    setCurrentPage(1)
  }, 300)

  // Handle Sorting Change
  const handleSortChange = (option) => {
    setSortOption(option)
    let sortedMembers = [...filteredMembers]

    if (option === 'Newest Members') {
      sortedMembers.sort(
        (a, b) => new Date(b.joined_date) - new Date(a.joined_date)
      )
    } else if (option === 'Alphabetical') {
      sortedMembers.sort((a, b) => a.first_name.localeCompare(b.first_name))
    }

    setFilteredMembers(sortedMembers)
  }

  const indexOfLastMember = currentPage * itemsPerPage
  const indexOfFirstMember = indexOfLastMember - itemsPerPage
  const currentMembers = filteredMembers.slice(
    indexOfFirstMember,
    indexOfLastMember
  )
  const totalPages = Math.ceil(filteredMembers.length / itemsPerPage)

  // Member Card Skeleton
  const MemberCardSkeleton = () => (
    <Card 
      className="h-100 member-card" 
      style={{ 
        borderRadius: '15px',
        overflow: 'hidden',
        border: 'none',
        boxShadow: '0 5px 15px rgba(0, 0, 0, 0.08)',
      }}
    >
      <div className="d-flex flex-column align-items-center p-4">
        <div 
          className="skeleton-avatar mb-3" 
          style={{ 
            width: '100px', 
            height: '100px', 
            borderRadius: '50%', 
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)', 
            backgroundSize: '200% 100%', 
            animation: 'shimmer 1.5s infinite' 
          }}
        ></div>
        
        <div 
          className="skeleton-name mb-2" 
          style={{ 
            height: '24px', 
            width: '70%', 
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)', 
            backgroundSize: '200% 100%', 
            animation: 'shimmer 1.5s infinite',
            borderRadius: '4px'
          }}
        ></div>
        
        <div 
          className="skeleton-title mb-3" 
          style={{ 
            height: '18px', 
            width: '50%', 
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)', 
            backgroundSize: '200% 100%', 
            animation: 'shimmer 1.5s infinite',
            borderRadius: '4px'
          }}
        ></div>
        
        <div 
          className="skeleton-button" 
          style={{ 
            height: '38px', 
            width: '80%', 
            background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)', 
            backgroundSize: '200% 100%', 
            animation: 'shimmer 1.5s infinite',
            borderRadius: '20px'
          }}
        ></div>
      </div>
    </Card>
  )

  return (
    <div>
      <Header />
      <div className='container-fluid'></div>
       <div className="wrapper" style={{ display: "flex" }}>
            <StudentSdebar onToggle={setSidebarExpanded} />
      
            <motion.div
              className="main-content p-lg-4 p-md-3 p-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              style={{
                marginTop: '60px', // Adjust this to match your Header height
                marginLeft: sidebarExpanded ? '260px' : '90px',
                transition: 'margin-left 0.25s ease-in-out',
                width: sidebarExpanded ? 'calc(100% - 260px)' : 'calc(100% - 90px)',
                overflowY: 'auto',
                height: 'calc(100vh - 60px)',
              }}
            >
        <Container className='py-1'>
          {/* Page Header */}
          <div className='d-flex justify-content-between align-items-center mb-4'>
            <h1 className='fw-bold'>Members</h1>
            <InputGroup
              style={{
                width: '300px',
                borderRadius: '50px',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                backgroundColor: '#fff',
                alignItems: 'center',
              }}
            >
              <InputGroup.Text
                style={{
                  backgroundColor: '#fff',
                  border: 'none',
                  paddingLeft: '14px',
                  paddingRight: '8px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Search size={18} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search Members"
                style={{
                  border: 'none',
                  boxShadow: 'none',
                  padding: '10px 12px',
                  fontSize: '14px',
                }}
                onChange={(e) => handleSearch(e.target.value)}
              />
              {searchTerm && (
                <Button
                  variant="light"
                  onClick={() => {
                    setSearchTerm('')
                    handleSearch('')
                  }}
                  style={{
                    border: 'none',
                    backgroundColor: 'transparent',
                    paddingRight: '14px',
                    paddingLeft: '8px',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <X size={18} />
                </Button>
              )}
            </InputGroup>
          </div>

          {/* Navigation Tabs */}
          <Nav variant='tabs' className='mb-4'>
            <Nav.Item>
              <Nav.Link active className='fw-bold'>
                All Members{' '}
                <Badge
                  style={{ backgroundColor: '#B54EA5', color: 'white' }}
                  pill
                >
                  {filteredMembers.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {/* Sorting & View Controls */}
          <div className='d-flex justify-content-between align-items-center mb-4'>
            <div className='d-flex gap-2'>
              <Dropdown>
                <Dropdown.Toggle
                  style={{
                    backgroundColor: '#B54EA5',
                    borderColor: '#B54EA5',
                    color: 'white',
                  }}
                >
                  {sortOption} 
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {sortingOptions.map((option, index) => (
                    <Dropdown.Item
                      key={index}
                      onClick={() => handleSortChange(option)}
                    >
                      {option}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>

              {/* View Toggle Buttons */}
              <div className='btn-group'>
                <Button
                  style={{
                    backgroundColor: view === 'grid' ? '#B54EA5' : 'white',
                    borderColor: '#B54EA5',
                    color: view === 'grid' ? 'white' : '#B54EA5',
                  }}
                  onClick={() => setView('grid')}
                >
                  <Grid />
                </Button>
                <Button
                  style={{
                    backgroundColor: view === 'list' ? '#B54EA5' : 'white',
                    borderColor: '#B54EA5',
                    color: view === 'list' ? 'white' : '#B54EA5',
                  }}
                  onClick={() => setView('list')}
                >
                  <List />
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Row
                xs={1}
                md={view === 'grid' ? 2 : 1}
                lg={view === 'grid' ? 3 : 1}
                xl={view === 'grid' ? 4 : 1}
                className='g-4'
              >
                {Array(8).fill(0).map((_, index) => (
                  <Col key={index}>
                    <MemberCardSkeleton />
                  </Col>
                ))}
              </Row>
            </motion.div>
          ) : (
            <>
              {currentMembers.length > 0 ? (
                <Row
                  xs={1}
                  md={view === 'grid' ? 2 : 1}
                  lg={view === 'grid' ? 3 : 1}
                  xl={view === 'grid' ? 4 : 1}
                  className='g-4'
                >
                  {currentMembers.map((member) => (
                    <Col key={member.id}>
                      <MemberCard member={member} view={view} />
                    </Col>
                  ))}
                </Row>
              ) : (
                <p className='text-center text-muted'>No members found.</p>
              )}
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination className='mt-4 justify-content-center'>
              <Pagination.First
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
              />
              <Pagination.Prev
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              />
              {[...Array(totalPages).keys()].map((number) => (
                <Pagination.Item
                  key={number + 1}
                  active={number + 1 === currentPage}
                  onClick={() => setCurrentPage(number + 1)}
                >
                  {number + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          )}
        </Container>
        </motion.div>
      </div>
      <style jsx>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  )
}

// import {
//   Container,
//   Row,
//   Col,
//   Form,
//   Button,
//   Dropdown,
//   InputGroup,
//   Nav,
//   Badge,
//   Pagination,
// } from 'react-bootstrap'
// import { Search, Grid, List, ChevronDown } from 'react-bootstrap-icons'
// import { MemberCard } from '../Components/MemberPage/Member-card.jsx'
// import Header from '../Components/Header.jsx'
// import StudentSdebar from './StudentSdebar.jsx'
// import axios from 'axios'
// import { debounce } from 'lodash'
// import {  X } from 'react-feather';

// export default function MembersPage() {
//   const [members, setMembers] = useState([])
//   const [filteredMembers, setFilteredMembers] = useState([])
//   const [loading, setLoading] = useState(false)
//   const [searchTerm, setSearchTerm] = useState('')
//   const [view, setView] = useState('grid')
//   const [currentPage, setCurrentPage] = useState(1)
//   const [sortOption, setSortOption] = useState('Recently Active') // Default sort option
//   const itemsPerPage = 8

//   // Sorting Options (Dynamic)
//   const sortingOptions = ['Recently Active', 'Newest Members', 'Alphabetical']

//   useEffect(() => {
//     const fetchUserData = async () => {
//       setLoading(true)
//       const token = localStorage.getItem('jwttoken')
//       if (!token) return

//       try {
//         const response = await axios.get(
//           'https://kaasbackend.vetrinahealthcare.com/socialapp/users/',
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//               'Content-Type': 'application/json',
//             },
//           }
//         )

//         if (response.status === 200) {
//           // Make sure the API response includes the 'is_connected' status
//           const membersWithStatus = response.data.map((member) => ({
//             ...member,
//             is_friends: member.is_friends ?? false, // Default to false// Default to false if not provided
//           }))
//           setMembers(membersWithStatus)
//           setFilteredMembers(membersWithStatus)
//         }
//       } catch (error) {
//         console.error('Error fetching user data:', error)
//       } finally {
//         setLoading(false)
//       }
//     }

//     fetchUserData()
//   }, [])

//   // Search Function (Optimized with Debounce)
//   const handleSearch = debounce((query) => {
//     setSearchTerm(query)
//     if (!query) {
//       setFilteredMembers(members)
//       return
//     }
//     const lowerCaseQuery = query.toLowerCase()
//     const filtered = members.filter((member) =>
//       `${member.first_name} ${member.last_name}`
//         .toLowerCase()
//         .includes(lowerCaseQuery)
//     )
//     setFilteredMembers(filtered)
//     setCurrentPage(1)
//   }, 300)

//   // Handle Sorting Change
//   const handleSortChange = (option) => {
//     setSortOption(option)
//     let sortedMembers = [...filteredMembers]

//     if (option === 'Newest Members') {
//       sortedMembers.sort(
//         (a, b) => new Date(b.joined_date) - new Date(a.joined_date)
//       )
//     } else if (option === 'Alphabetical') {
//       sortedMembers.sort((a, b) => a.first_name.localeCompare(b.first_name))
//     }

//     setFilteredMembers(sortedMembers)
//   }

//   const indexOfLastMember = currentPage * itemsPerPage
//   const indexOfFirstMember = indexOfLastMember - itemsPerPage
//   const currentMembers = filteredMembers.slice(
//     indexOfFirstMember,
//     indexOfLastMember
//   )
//   const totalPages = Math.ceil(filteredMembers.length / itemsPerPage)

//   return (
//     <div>
//       <Header />
//       <div className='container-fluid'></div>
//       <div className="wrapper" style={{ marginTop: '5.5rem' }}>
//       <StudentSdebar />
//         <Container className='py-1'>
//           {/* Page Header */}
//           <div className='d-flex justify-content-between align-items-center mb-4'>
//             <h1 className='fw-bold'>Members</h1>
//             {/* <InputGroup style={{ width: '300px' }}>
//               <InputGroup.Text className='bg-white border-end-0'>
//                 <Search />
//               </InputGroup.Text>
//               <Form.Control
//                 placeholder='Search Members'
//                 className='border-start-0'
//                 style={{ boxShadow: 'none' }}
//                 onChange={(e) => handleSearch(e.target.value)}
//               />
//             </InputGroup> */}
//             <InputGroup
//   style={{
//     width: '300px',
//     borderRadius: '50px',
//     boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
//     overflow: 'hidden',
//     backgroundColor: '#fff',
//     alignItems: 'center',
//   }}
// >
//   <InputGroup.Text
//     style={{
//       backgroundColor: '#fff',
//       border: 'none',
//       paddingLeft: '14px',
//       paddingRight: '8px',
//       display: 'flex',
//       alignItems: 'center',
//     }}
//   >
//     <Search size={18} />
//   </InputGroup.Text>
//   <Form.Control
//     placeholder="Search Members"
//     style={{
//       border: 'none',
//       boxShadow: 'none',
//       padding: '10px 12px',
//       fontSize: '14px',
//     }}
//     onChange={(e) => handleSearch(e.target.value)}
//   />
//   {searchTerm && (
//     <Button
//       variant="light"
//       onClick={() => setSearchTerm('')}
//       style={{
//         border: 'none',
//         backgroundColor: 'transparent',
//         paddingRight: '14px',
//         paddingLeft: '8px',
//         display: 'flex',
//         alignItems: 'center',
//       }}
//     >
//       <X size={18} />
//     </Button>
//   )}
// </InputGroup>
//           </div>

//           {/* Navigation Tabs */}
//           <Nav variant='tabs' className='mb-4'>
//             <Nav.Item>
//               <Nav.Link active className='fw-bold'>
//                 All Members{' '}
//                 <Badge
//                   style={{ backgroundColor: '#B54EA5', color: 'white' }}
//                   pill
//                 >
//                   {filteredMembers.length}
//                 </Badge>
//               </Nav.Link>
//             </Nav.Item>
//           </Nav>

//           {/* Sorting & View Controls */}
//           <div className='d-flex justify-content-between align-items-center mb-4'>
//             <div className='d-flex gap-2'>
//               <Dropdown>
//                 <Dropdown.Toggle
//                   style={{
//                     backgroundColor: '#B54EA5',
//                     borderColor: '#B54EA5',
//                     color: 'white',
//                   }}
//                 >
//                   {sortOption} 
//                 </Dropdown.Toggle>
//                 <Dropdown.Menu>
//                   {sortingOptions.map((option, index) => (
//                     <Dropdown.Item
//                       key={index}
//                       onClick={() => handleSortChange(option)}
//                     >
//                       {option}
//                     </Dropdown.Item>
//                   ))}
//                 </Dropdown.Menu>
//               </Dropdown>

//               {/* View Toggle Buttons */}
//               <div className='btn-group'>
//                 <Button
//                   style={{
//                     backgroundColor: view === 'grid' ? '#B54EA5' : 'white',
//                     borderColor: '#B54EA5',
//                     color: view === 'grid' ? 'white' : '#B54EA5',
//                   }}
//                   onClick={() => setView('grid')}
//                 >
//                   <Grid />
//                 </Button>
//                 <Button
//                   style={{
//                     backgroundColor: view === 'list' ? '#B54EA5' : 'white',
//                     borderColor: '#B54EA5',
//                     color: view === 'list' ? 'white' : '#B54EA5',
//                   }}
//                   onClick={() => setView('list')}
//                 >
//                   <List />
//                 </Button>
//               </div>
//             </div>
//           </div>

//           {loading ? (
//             <div
//               className='d-flex justify-content-center align-items-center'
//               style={{ minHeight: '200px' }}
//             >
//               <div className='spinner-border text-primary' role='status'>
//                 <span className='visually-hidden'>Loading...</span>
//               </div>
//             </div>
//           ) : (
//             <>
//               {currentMembers.length > 0 ? (
//                 <Row
//                   xs={1}
//                   md={view === 'grid' ? 2 : 1}
//                   lg={view === 'grid' ? 3 : 1}
//                   xl={view === 'grid' ? 4 : 1}
//                   className='g-4'
//                 >
//                   {currentMembers.map((member) => (
//                     <Col key={member.id}>
//                       <MemberCard member={member} view={view} />
//                     </Col>
//                   ))}
//                 </Row>
//               ) : (
//                 <p className='text-center text-muted'>No members found.</p>
//               )}
//             </>
//           )}

//           {/* Pagination */}
//           {totalPages > 1 && (
//             <Pagination className='mt-4 justify-content-center'>
//               <Pagination.First
//                 onClick={() => setCurrentPage(1)}
//                 disabled={currentPage === 1}
//               />
//               <Pagination.Prev
//                 onClick={() => setCurrentPage(currentPage - 1)}
//                 disabled={currentPage === 1}
//               />
//               {[...Array(totalPages).keys()].map((number) => (
//                 <Pagination.Item
//                   key={number + 1}
//                   active={number + 1 === currentPage}
//                   onClick={() => setCurrentPage(number + 1)}
//                 >
//                   {number + 1}
//                 </Pagination.Item>
//               ))}
//               <Pagination.Next
//                 onClick={() => setCurrentPage(currentPage + 1)}
//                 disabled={currentPage === totalPages}
//               />
//               <Pagination.Last
//                 onClick={() => setCurrentPage(totalPages)}
//                 disabled={currentPage === totalPages}
//               />
//             </Pagination>
//           )}
//         </Container>
//       </div>
//     </div>
//   )
// }
// import React, { useState, useEffect } from 'react';
// import {
//   Container,
//   Row,
//   Col,
//   Form,
//   Button,
//   Dropdown,
//   InputGroup,
//   Nav,
//   Badge,
//   Pagination,
//   Card,
//   Image,
//   Tabs,
//   Tab,
//   Alert,
//   Spinner,
//   Modal,
//   ListGroup,
//   OverlayTrigger,
//   Tooltip
// } from 'react-bootstrap';
// import { 
//   Search, 
//   Grid, 
//   List, 
//   ChevronDown, 
//   PersonAdd, 
//   PersonCheck, 
//   PersonDash, 
//   Filter, 
//   SortAlphaDown, 
//   Clock, 
//   People, 
//   PersonBadge, 
//   PersonPlus, 
//   X, 
//   Check, 
//   ThreeDots 
// } from 'react-bootstrap-icons';
// import axios from 'axios';
// import { debounce } from 'lodash';

// // Header Component
// const Header = () => {
//   return (
//     <div className="bg-white shadow-sm fixed-top">
//       <div className="container d-flex justify-content-between align-items-center py-2">
//         <h3 className="m-0 text-primary">Social App</h3>
//         <div className="d-flex align-items-center">
//           <img 
//             src="https://via.placeholder.com/40" 
//             alt="Profile" 
//             className="rounded-circle me-2" 
//           />
//           <Dropdown>
//             <Dropdown.Toggle variant="light" id="dropdown-basic" className="border-0">
//               My Account
//             </Dropdown.Toggle>
//             <Dropdown.Menu>
//               <Dropdown.Item href="#">Profile</Dropdown.Item>
//               <Dropdown.Item href="#">Settings</Dropdown.Item>
//               <Dropdown.Divider />
//               <Dropdown.Item href="#">Logout</Dropdown.Item>
//             </Dropdown.Menu>
//           </Dropdown>
//         </div>
//       </div>
//     </div>
//   );
// };

// // Sidebar Component
// const Sidebar = () => {
//   return (
//     <div className="sidebar bg-light h-100 position-fixed" style={{ width: '220px', paddingTop: '70px' }}>
//       <ListGroup variant="flush">
//         <ListGroup.Item action href="#" className="border-0 py-3">
//           <i className="bi bi-house-door me-2"></i> Home
//         </ListGroup.Item>
//         <ListGroup.Item action href="#" className="border-0 py-3 bg-primary text-white">
//           <i className="bi bi-people me-2"></i> Members
//         </ListGroup.Item>
//         <ListGroup.Item action href="#" className="border-0 py-3">
//           <i className="bi bi-chat-dots me-2"></i> Messages
//         </ListGroup.Item>
//         <ListGroup.Item action href="#" className="border-0 py-3">
//           <i className="bi bi-calendar-event me-2"></i> Events
//         </ListGroup.Item>
//         <ListGroup.Item action href="#" className="border-0 py-3">
//           <i className="bi bi-gear me-2"></i> Settings
//         </ListGroup.Item>
//       </ListGroup>
//     </div>
//   );
// };

// // Member Card Component
// const MemberCard = ({ member, view, onSendFriendRequest, onCancelFriendRequest }) => {
//   const [requestSent, setRequestSent] = useState(false);
//   const [requestLoading, setRequestLoading] = useState(false);
//   const [error, setError] = useState(null);

//   const handleSendRequest = async () => {
//     setRequestLoading(true);
//     try {
//       await onSendFriendRequest(member.user_id);
//       setRequestSent(true);
//       setError(null);
//     } catch (error) {
//       if (error.response && error.response.data && error.response.data.detail) {
//         setError(error.response.data.detail);
//       } else {
//         setError("Failed to send friend request");
//       }
//     } finally {
//       setRequestLoading(false);
//     }
//   };

//   const handleCancelRequest = async () => {
//     setRequestLoading(true);
//     try {
//       await onCancelFriendRequest(member.user_id);
//       setRequestSent(false);
//       setError(null);
//     } catch (error) {
//       setError("Failed to cancel friend request");
//     } finally {
//       setRequestLoading(false);
//     }
//   };

//   useEffect(() => {
//     // Reset state when member changes
//     setRequestSent(member.is_friend_request_sent || false);
//     setError(null);
//   }, [member]);

//   if (view === 'grid') {
//     return (
//       <Card className="h-100 shadow-sm hover-shadow">
//         <div className="position-relative">
//           <Card.Img 
//             variant="top" 
//             src={member.profile_pic || "https://via.placeholder.com/300x180?text=No+Profile+Image"} 
//             alt={member.full_name}
//             style={{ height: '180px', objectFit: 'cover' }}
//           />
//           <div 
//             className={`position-absolute top-0 end-0 m-2 badge ${member.is_friends ? 'bg-success' : 'bg-secondary'}`}
//           >
//             {member.is_friends ? 'Friend' : 'Not Connected'}
//           </div>
//         </div>
//         <Card.Body className="d-flex flex-column">
//           <Card.Title className="mb-1">{member.full_name}</Card.Title>
//           <Card.Text className="text-muted small mb-3">
//             {member.email}
//           </Card.Text>
//           <div className="mt-auto">
//             {member.is_friends ? (
//               <Button variant="outline-success" className="w-100" disabled>
//                 <PersonCheck className="me-2" /> Connected
//               </Button>
//             ) : requestSent ? (
//               <Button 
//                 variant="outline-secondary" 
//                 className="w-100" 
//                 onClick={handleCancelRequest}
//                 disabled={requestLoading}
//               >
//                 {requestLoading ? (
//                   <Spinner animation="border" size="sm" className="me-2" />
//                 ) : (
//                   <PersonDash className="me-2" />
//                 )}
//                 Request Sent
//               </Button>
//             ) : (
//               <Button 
//                 variant="outline-primary" 
//                 className="w-100" 
//                 onClick={handleSendRequest}
//                 disabled={requestLoading}
//               >
//                 {requestLoading ? (
//                   <Spinner animation="border" size="sm" className="me-2" />
//                 ) : (
//                   <PersonAdd className="me-2" />
//                 )}
//                 Connect
//               </Button>
//             )}
//           </div>
//           {error && (
//             <Alert variant="danger" className="mt-2 p-2 small">
//               {error}
//             </Alert>
//           )}
//         </Card.Body>
//       </Card>
//     );
//   } else {
//     return (
//       <Card className="mb-2 shadow-sm hover-shadow">
//         <Card.Body>
//           <div className="d-flex align-items-center">
//             <Image 
//               src={member.profile_pic || "https://via.placeholder.com/100?text=No+Image"} 
//               roundedCircle 
//               style={{ width: '60px', height: '60px', objectFit: 'cover' }}
//               className="me-3"
//             />
//             <div className="flex-grow-1">
//               <h5 className="mb-1">{member.full_name}</h5>
//               <p className="text-muted mb-0 small">{member.email}</p>
//             </div>
//             <div>
//               {member.is_friends ? (
//                 <Button variant="outline-success" size="sm">
//                   <PersonCheck className="me-2" /> Connected
//                 </Button>
//               ) : requestSent ? (
//                 <Button 
//                   variant="outline-secondary" 
//                   size="sm"
//                   onClick={handleCancelRequest}
//                   disabled={requestLoading}
//                 >
//                   {requestLoading ? (
//                     <Spinner animation="border" size="sm" className="me-2" />
//                   ) : (
//                     <PersonDash className="me-2" />
//                   )}
//                   Request Sent
//                 </Button>
//               ) : (
//                 <Button 
//                   variant="outline-primary" 
//                   size="sm"
//                   onClick={handleSendRequest}
//                   disabled={requestLoading}
//                 >
//                   {requestLoading ? (
//                     <Spinner animation="border" size="sm" className="me-2" />
//                   ) : (
//                     <PersonAdd className="me-2" />
//                   )}
//                   Connect
//                 </Button>
//               )}
//             </div>
//           </div>
//           {error && (
//             <Alert variant="danger" className="mt-2 p-2 small">
//               {error}
//             </Alert>
//           )}
//         </Card.Body>
//       </Card>
//     );
//   }
// };

// // Main Component
// function SocialApp() {
//   // States
//   const [members, setMembers] = useState([]);
//   const [filteredMembers, setFilteredMembers] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [view, setView] = useState('grid');
//   const [currentPage, setCurrentPage] = useState(1);
//   const [sortOption, setSortOption] = useState('Recently Active');
//   const [activeTab, setActiveTab] = useState('all');
//   const [groups, setGroups] = useState([
//     { id: 'friends', name: 'Friends', count: 0 },
//     { id: 'pending', name: 'Pending Requests', count: 0 },
//     { id: 'suggested', name: 'Suggested', count: 0 }
//   ]);
//   const [error, setError] = useState(null);
//   const [successMessage, setSuccessMessage] = useState(null);
  
//   const itemsPerPage = 8;

//   // Sorting Options
//   const sortingOptions = ['Recently Active', 'Newest Members', 'Alphabetical'];

//   // Fetch user data
//   useEffect(() => {
//     const fetchUserData = async () => {
//       setLoading(true);
//       setError(null);
      
//       const token = localStorage.getItem('jwttoken');
//       if (!token) {
//         setError("Authentication token not found. Please login again.");
//         setLoading(false);
//         return;
//       }

//       try {
//         const response = await axios.get(
//           'https://kaasbackend.vetrinahealthcare.com/socialapp/users/',
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//               'Content-Type': 'application/json',
//             },
//           }
//         );

//         if (response.status === 200) {
//           // Process the member data
//           const membersWithStatus = response.data.map((member) => ({
//             ...member,
//             is_friends: member.is_friends ?? false,
//             is_friend_request_sent: member.is_friend_request_sent ?? false
//           }));
          
//           setMembers(membersWithStatus);
//           setFilteredMembers(membersWithStatus);
          
//           // Update group counts
//           const friendsCount = membersWithStatus.filter(m => m.is_friends).length;
//           const pendingCount = membersWithStatus.filter(m => m.is_friend_request_sent && !m.is_friends).length;
          
//           setGroups([
//             { id: 'all', name: 'All Members', count: membersWithStatus.length },
//             { id: 'friends', name: 'Friends', count: friendsCount },
//             { id: 'pending', name: 'Pending Requests', count: pendingCount },
//             { id: 'suggested', name: 'Suggested', count: membersWithStatus.length - friendsCount - pendingCount }
//           ]);
//         }
//       } catch (error) {
//         console.error('Error fetching user data:', error);
//         setError("Failed to load members. Please try again later.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchUserData();
//   }, []);

//   // Handle search
//   const handleSearch = debounce((query) => {
//     setSearchTerm(query);
//     if (!query) {
//       filterMembersByTab(activeTab, members);
//       return;
//     }
    
//     const lowerCaseQuery = query.toLowerCase();
//     const filtered = members.filter((member) =>
//       `${member.first_name} ${member.last_name}`
//         .toLowerCase()
//         .includes(lowerCaseQuery)
//     );
    
//     filterMembersByTab(activeTab, filtered);
//     setCurrentPage(1);
//   }, 300);

//   // Handle sorting
//   const handleSortChange = (option) => {
//     setSortOption(option);
//     let sortedMembers = [...filteredMembers];

//     if (option === 'Newest Members') {
//       sortedMembers.sort(
//         (a, b) => new Date(b.joined_date || Date.now()) - new Date(a.joined_date || Date.now())
//       );
//     } else if (option === 'Alphabetical') {
//       sortedMembers.sort((a, b) => a.first_name.localeCompare(b.first_name));
//     } else if (option === 'Recently Active') {
//       // Default sorting or by last activity if available
//       sortedMembers.sort((a, b) => b.user_id - a.user_id);
//     }

//     setFilteredMembers(sortedMembers);
//   };

//   // Filter members by tab
//   const filterMembersByTab = (tab, membersToFilter = members) => {
//     let filtered = membersToFilter;
    
//     if (tab === 'friends') {
//       filtered = membersToFilter.filter(member => member.is_friends);
//     } else if (tab === 'pending') {
//       filtered = membersToFilter.filter(member => member.is_friend_request_sent && !member.is_friends);
//     } else if (tab === 'suggested') {
//       filtered = membersToFilter.filter(member => !member.is_friends && !member.is_friend_request_sent);
//     }
    
//     setFilteredMembers(filtered);
//     setCurrentPage(1);
//   };

//   // Handle tab change
//   const handleTabChange = (tab) => {
//     setActiveTab(tab);
//     filterMembersByTab(tab);
//   };

//   // Send friend request
//   const handleSendFriendRequest = async (userId) => {
//     const token = localStorage.getItem('jwttoken');
//     if (!token) {
//       throw new Error("Authentication token not found");
//     }

//     try {
//       const response = await axios.post(
//         'https://kaasbackend.vetrinahealthcare.com/socialapp/friend_requests/send/',
//         { user_id: userId },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             'Content-Type': 'application/json',
//           },
//         }
//       );

//       if (response.status === 200 || response.status === 201) {
//         // Update the member's status in the state
//         const updatedMembers = members.map(member => 
//           member.user_id === userId 
//             ? { ...member, is_friend_request_sent: true } 
//             : member
//         );
        
//         setMembers(updatedMembers);
//         filterMembersByTab(activeTab, updatedMembers);
        
//         // Update group counts
//         const friendsCount = updatedMembers.filter(m => m.is_friends).length;
//         const pendingCount = updatedMembers.filter(m => m.is_friend_request_sent && !m.is_friends).length;
        
//         setGroups([
//           { id: 'all', name: 'All Members', count: updatedMembers.length },
//           { id: 'friends', name: 'Friends', count: friendsCount },
//           { id: 'pending', name: 'Pending Requests', count: pendingCount },
//           { id: 'suggested', name: 'Suggested', count: updatedMembers.length - friendsCount - pendingCount }
//         ]);
        
//         setSuccessMessage("Friend request sent successfully!");
//         setTimeout(() => setSuccessMessage(null), 3000);
        
//         return response.data;
//       }
//     } catch (error) {
//       console.error('Error sending friend request:', error);
//       throw error;
//     }
//   };

//   // Cancel friend request
//   const handleCancelFriendRequest = async (userId) => {
//     const token = localStorage.getItem('jwttoken');
//     if (!token) {
//       throw new Error("Authentication token not found");
//     }

//     try {
//       const response = await axios.post(
//         'https://kaasbackend.vetrinahealthcare.com/socialapp/friend_requests/cancel/',
//         { user_id: userId },
//         {
//           headers: {
//             Authorization: `Bearer ${token}`,
//             'Content-Type': 'application/json',
//           },
//         }
//       );

//       if (response.status === 200 || response.status === 204) {
//         // Update the member's status in the state
//         const updatedMembers = members.map(member => 
//           member.user_id === userId 
//             ? { ...member, is_friend_request_sent: false } 
//             : member
//         );
        
//         setMembers(updatedMembers);
//         filterMembersByTab(activeTab, updatedMembers);
        
//         // Update group counts
//         const friendsCount = updatedMembers.filter(m => m.is_friends).length;
//         const pendingCount = updatedMembers.filter(m => m.is_friend_request_sent && !m.is_friends).length;
        
//         setGroups([
//           { id: 'all', name: 'All Members', count: updatedMembers.length },
//           { id: 'friends', name: 'Friends', count: friendsCount },
//           { id: 'pending', name: 'Pending Requests', count: pendingCount },
//           { id: 'suggested', name: 'Suggested', count: updatedMembers.length - friendsCount - pendingCount }
//         ]);
        
//         setSuccessMessage("Friend request cancelled successfully!");
//         setTimeout(() => setSuccessMessage(null), 3000);
        
//         return response.data;
//       }
//     } catch (error) {
//       console.error('Error cancelling friend request:', error);
//       throw error;
//     }
//   };

//   // Pagination
//   const indexOfLastMember = currentPage * itemsPerPage;
//   const indexOfFirstMember = indexOfLastMember - itemsPerPage;
//   const currentMembers = filteredMembers.slice(indexOfFirstMember, indexOfLastMember);
//   const totalPages = Math.ceil(filteredMembers.length / itemsPerPage);

//   return (
//     <div className="bg-light min-vh-100">
//       <Header />
      
//       <div className="wrapper d-flex" style={{ paddingTop: '56px' }}>
//         <Sidebar />
        
//         <div className="content-wrapper flex-grow-1" style={{ marginLeft: '220px', padding: '20px' }}>
//           {successMessage && (
//             <Alert variant="success" className="position-fixed top-0 end-0 m-3 shadow-sm" style={{ zIndex: 1050 }}>
//               {successMessage}
//             </Alert>
//           )}
          
//           {/* Page Header */}
//           <div className="d-flex justify-content-between align-items-center mb-4">
//             <h1 className="fw-bold">Members</h1>
//             <InputGroup style={{ width: '300px' }}>
//               <InputGroup.Text className="bg-white border-end-0">
//                 <Search />
//               </InputGroup.Text>
//               <Form.Control
//                 placeholder="Search Members..."
//                 className="border-start-0"
//                 style={{ boxShadow: 'none' }}
//                 onChange={(e) => handleSearch(e.target.value)}
//               />
//             </InputGroup>
//           </div>

//           {/* Navigation Tabs */}
//           <Tabs
//             activeKey={activeTab}
//             onSelect={handleTabChange}
//             className="mb-4 nav-tabs-custom"
//           >
//             {groups.map(group => (
//               <Tab 
//                 key={group.id} 
//                 eventKey={group.id} 
//                 title={
//                   <span>
//                     {group.name} <Badge bg="primary" pill>{group.count}</Badge>
//                   </span>
//                 }
//               />
//             ))}
//           </Tabs>

//           {/* Sorting & View Controls */}
//           <div className="d-flex justify-content-between align-items-center mb-4">
//             <div className="d-flex gap-2">
//               <Dropdown>
//                 <Dropdown.Toggle
//                   variant="primary"
//                   className="d-flex align-items-center"
//                 >
//                   <Filter className="me-2" /> {sortOption} <ChevronDown className="ms-2" />
//                 </Dropdown.Toggle>
//                 <Dropdown.Menu>
//                   {sortingOptions.map((option, index) => (
//                     <Dropdown.Item
//                       key={index}
//                       onClick={() => handleSortChange(option)}
//                       active={sortOption === option}
//                     >
//                       {option === 'Alphabetical' && <SortAlphaDown className="me-2" />}
//                       {option === 'Recently Active' && <Clock className="me-2" />}
//                       {option === 'Newest Members' && <People className="me-2" />}
//                       {option}
//                     </Dropdown.Item>
//                   ))}
//                 </Dropdown.Menu>
//               </Dropdown>

//               {/* View Toggle Buttons */}
//               <div className="btn-group">
//                 <Button
//                   variant={view === 'grid' ? 'primary' : 'light'}
//                   onClick={() => setView('grid')}
//                   className="d-flex align-items-center"
//                 >
//                   <Grid className="me-1" /> Grid
//                 </Button>
//                 <Button
//                   variant={view === 'list' ? 'primary' : 'light'}
//                   onClick={() => setView('list')}
//                   className="d-flex align-items-center"
//                 >
//                   <List className="me-1" /> List
//                 </Button>
//               </div>
//             </div>
//           </div>

//           {/* Error Message */}
//           {error && (
//             <Alert variant="danger" className="mb-4">
//               <Alert.Heading>Error</Alert.Heading>
//               <p>{error}</p>
//             </Alert>
//           )}

//           {/* Loading Indicator */}
//           {loading ? (
//             <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
//               <Spinner animation="border" role="status" variant="primary">
//                 <span className="visually-hidden">Loading...</span>
//               </Spinner>
//             </div>
//           ) : (
//             <>
//               {currentMembers.length > 0 ? (
//                 <Row
//                   xs={1}
//                   md={view === 'grid' ? 2 : 1}
//                   lg={view === 'grid' ? 3 : 1}
//                   xl={view === 'grid' ? 4 : 1}
//                   className="g-4"
//                 >
//                   {currentMembers.map((member) => (
//                     <Col key={member.user_id}>
//                       <MemberCard 
//                         member={member} 
//                         view={view} 
//                         onSendFriendRequest={handleSendFriendRequest}
//                         onCancelFriendRequest={handleCancelFriendRequest}
//                       />
//                     </Col>
//                   ))}
//                 </Row>
//               ) : (
//                 <div className="text-center py-5">
//                   <div className="mb-3">
//                     <PersonBadge size={50} className="text-muted" />
//                   </div>
//                   <h4 className="text-muted">No members found</h4>
//                   <p className="text-muted">
//                     {activeTab !== 'all' 
//                       ? `There are no members in the "${groups.find(g => g.id === activeTab)?.name}" category.` 
//                       : "Try adjusting your search or filters."}
//                   </p>
//                 </div>
//               )}
//             </>
//           )}

//           {/* Pagination */}
//           {totalPages > 1 && (
//             <div className="d-flex justify-content-center mt-4">
//               <Pagination>
//                 <Pagination.First
//                   onClick={() => setCurrentPage(1)}
//                   disabled={currentPage === 1}
//                 />
//                 <Pagination.Prev
//                   onClick={() => setCurrentPage(currentPage - 1)}
//                   disabled={currentPage === 1}
//                 />
                
//                 {[...Array(totalPages).keys()].map((number) => {
//                   // Show limited page numbers with ellipsis
//                   if (
//                     number === 0 || 
//                     number === totalPages - 1 || 
//                     (number >= currentPage - 2 && number <= currentPage + 0)
//                   ) {
//                     return (
//                       <Pagination.Item
//                         key={number + 1}
//                         active={number + 1 === currentPage}
//                         onClick={() => setCurrentPage(number + 1)}
//                       >
//                         {number + 1}
//                       </Pagination.Item>
//                     );
//                   } else if (
//                     number === currentPage - 3 || 
//                     number === currentPage + 1
//                   ) {
//                     return <Pagination.Ellipsis key={`ellipsis-${number}`} />;
//                   }
//                   return null;
//                 })}
                
//                 <Pagination.Next
//                   onClick={() => setCurrentPage(currentPage + 1)}
//                   disabled={currentPage === totalPages}
//                 />
//                 <Pagination.Last
//                   onClick={() => setCurrentPage(totalPages)}
//                   disabled={currentPage === totalPages}
//                 />
//               </Pagination>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// }

// export default SocialApp;